import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Mail, Lock, Shield, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TurnstileWidget } from "@/components/Onboarding/TurnstileWidget";
import AuthLeftPanel from "@/components/Auth/AuthLeftPanel";

// Schema de validação com senha forte (12+ caracteres)
const registerSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z
    .string()
    .min(12, "Mínimo 12 caracteres")
    .regex(/[A-Z]/, "Deve conter uma letra maiúscula")
    .regex(/[a-z]/, "Deve conter uma letra minúscula")
    .regex(/[0-9]/, "Deve conter um número")
    .regex(/[^A-Za-z0-9]/, "Deve conter um caractere especial"),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "Você deve aceitar os termos de uso",
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

// Componente de requisitos de senha
function PasswordRequirements({ password }: { password: string }) {
  const requirements = [
    { label: "12+ caracteres", met: password.length >= 12 },
    { label: "Letra maiúscula", met: /[A-Z]/.test(password) },
    { label: "Letra minúscula", met: /[a-z]/.test(password) },
    { label: "Número", met: /[0-9]/.test(password) },
    { label: "Caractere especial", met: /[^A-Za-z0-9]/.test(password) },
  ];

  return (
    <div className="grid grid-cols-2 gap-1 text-xs mt-2">
      {requirements.map((req) => (
        <div
          key={req.label}
          className={`flex items-center gap-1 ${
            req.met ? "text-green-600" : "text-muted-foreground"
          }`}
        >
          <CheckCircle2 className={`h-3 w-3 ${req.met ? "opacity-100" : "opacity-30"}`} />
          {req.label}
        </div>
      ))}
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      acceptTerms: false,
    },
  });

  const password = watch("password") || "";
  const acceptTerms = watch("acceptTerms");

  const onSubmit = async (data: RegisterFormData) => {
    if (!captchaToken) {
      toast({
        title: "Verificação necessária",
        description: "Por favor, complete a verificação de segurança.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Verificar CAPTCHA
      const isDev = window.location.hostname === "localhost" || 
                   window.location.hostname.includes("lovable");
      
      if (!isDev) {
        const { data: captchaResult, error: captchaError } = await supabase.functions.invoke(
          "verify-captcha",
          { body: { token: captchaToken } }
        );

        if (captchaError || !captchaResult?.success) {
          throw new Error("Falha na verificação de segurança");
        }
      }

      // Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=/onboarding/personal`,
          data: {
            onboarding_step: "email_sent",
          },
        },
      });

      if (authError) {
        if (authError.message.includes("already registered")) {
          throw new Error("Este email já está cadastrado. Tente fazer login.");
        }
        throw authError;
      }

      if (!authData.user) {
        throw new Error("Erro ao criar usuário");
      }

      // Sucesso - mostrar tela de verificação
      setRegisteredEmail(data.email);
      setEmailSent(true);

      toast({
        title: "Conta criada com sucesso!",
        description: "Verifique seu email para continuar o cadastro.",
      });

    } catch (error: any) {
      console.error("[Register] Erro:", error);
      toast({
        title: "Erro no cadastro",
        description: error.message || "Ocorreu um erro ao criar sua conta.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Tela de email enviado
  if (emailSent) {
    return (
      <div className="min-h-screen w-full flex flex-col lg:flex-row">
        <AuthLeftPanel />
        <div 
          className="flex-1 flex items-center justify-center bg-gradient-to-tl from-amber-50 via-orange-50 to-white min-h-screen lg:min-h-0 p-4 sm:p-6 lg:p-8 overflow-y-auto"
          style={{
            backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Card className="w-full max-w-md border-2 border-amber-200 bg-white/95 backdrop-blur-sm shadow-2xl shadow-amber-200/20">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Verifique seu email</CardTitle>
              <CardDescription className="text-base mt-2">
                Enviamos um link de confirmação para:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="font-medium text-lg text-primary">{registeredEmail}</p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <p className="font-medium">Próximos passos:</p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Abra seu email</li>
                  <li>Clique no link de confirmação</li>
                  <li>Complete seu cadastro</li>
                </ol>
              </div>

              <div className="text-center text-sm text-muted-foreground">
                <p>Não recebeu o email?</p>
                <Button
                  variant="link"
                  className="p-0 h-auto"
                  onClick={async () => {
                    const { error } = await supabase.auth.resend({
                      type: "signup",
                      email: registeredEmail,
                      options: {
                        emailRedirectTo: `${window.location.origin}/auth/callback?next=/onboarding/personal`,
                      },
                    });
                    if (!error) {
                      toast({
                        title: "Email reenviado",
                        description: "Verifique sua caixa de entrada.",
                      });
                    }
                  }}
                >
                  Reenviar email de confirmação
                </Button>
              </div>

              <div className="text-center">
                <Link to="/auth" className="text-sm text-muted-foreground hover:text-primary">
                  ← Voltar para login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">
      <AuthLeftPanel />
      <div 
        className="flex-1 flex items-center justify-center bg-gradient-to-tl from-amber-50 via-orange-50 to-white min-h-screen lg:min-h-0 p-4 sm:p-6 lg:p-8 overflow-y-auto"
        style={{
          backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <Card className="w-full max-w-md border-2 border-amber-200 bg-white/95 backdrop-blur-sm shadow-2xl shadow-amber-200/20">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Criar Conta</CardTitle>
          <CardDescription>
            Cadastro seguro para sua empresa
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email corporativo</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@empresa.com"
                  className="pl-10"
                  {...register("email")}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Senha */}
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••••••"
                  className="pl-10 pr-10"
                  {...register("password")}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <PasswordRequirements password={password} />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {/* Confirmar Senha */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••••••"
                  className="pl-10 pr-10"
                  {...register("confirmPassword")}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Termos de Uso */}
            <div className="flex items-start space-x-2">
              <Checkbox
                id="acceptTerms"
                checked={acceptTerms}
                onCheckedChange={(checked) => setValue("acceptTerms", checked === true)}
              />
              <Label htmlFor="acceptTerms" className="text-sm leading-tight cursor-pointer">
                Li e aceito os{" "}
                <a href="/terms" className="text-primary hover:underline" target="_blank">
                  Termos de Uso
                </a>{" "}
                e a{" "}
                <a href="/privacy" className="text-primary hover:underline" target="_blank">
                  Política de Privacidade
                </a>
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-destructive">{errors.acceptTerms.message}</p>
            )}

            {/* CAPTCHA */}
            <div className="flex justify-center">
              <TurnstileWidget onVerify={setCaptchaToken} />
            </div>

            {/* Botão Submit */}
            <Button type="submit" className="w-full" disabled={isLoading || !captchaToken}>
              {isLoading ? "Criando conta..." : "Criar conta"}
            </Button>

            {/* Link para login */}
            <div className="text-center text-sm">
              <span className="text-muted-foreground">Já tem uma conta? </span>
              <Link to="/auth" className="text-primary hover:underline">
                Fazer login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
